import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  AdvertActionPayload,
  AdvertActionType,
  AdvertForm,
  ADVERTISER_TYPE_VALUES,
  OlxAdvertActionDraft,
} from './advert-action.interface';
import {
  OlxAdvertActionPayload,
  OlxAdvertiserType,
  OlxAdvertItem,
  OlxCategory,
  OlxCategoryAttribute,
  OlxCategoryAttributeType,
  OlxLocation,
  PLANNER_OLX_ACTION,
  PLANNER_OLX_ACTIONS_REGISTRY,
} from '../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { AI_TASK_TYPE, GenerateAdvertDescriptionPayload } from '../../../../../interfaces/ai.interface';
import { BaseService } from '../../../../../services/base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { phoneValidator } from '../../../../../validators/phone.validator';
import { Subject, takeUntil } from 'rxjs';
import { PowerOlxV2Service } from '../../../../../modules/crm/power-olx-v2/services/power-olx-v2.service';
import { RequestHelper } from '../../../../../services/request-helper.service';
import { SelectedCategoryEvent } from './components/advert-category-picker/advert-category-picker.interface';
import { DRAFT_ADVERT_LOCAL_STORAGE_KEY } from '../../../../../enum/constants';
import { NotificationService } from '../../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { locationRequiredValidator } from '../../../../../validators/location.validator';
import { PriceForm } from '../../../../price-editor/price-editor.interface';
import { ModalData } from '../../../../../model/modal-data.model';
import { SidebarData, SidebarType } from '../../stacked-sidebar.interface';
import { PLANNER_SCOPE, PlannerSidebarData, PlannerSidebarMode } from '../planner-v2/planner-v2.interface';
import { StackedSidebarComponent } from '../../stacked-sidebar.component';
import { AiService } from '../../../../../services/ai.service';
import { ModalService } from '../../../../../services/modal.service';
import { PlannerService } from '../../../../../services/planner.service';

@Component({
  selector: 'app-advert-action',
  templateUrl: './advert-action.component.html',
  styleUrl: './advert-action.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvertActionComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() payload!: AdvertActionPayload;
  @Output() close = new EventEmitter<string>();

  protected categories!: OlxCategory[];
  protected cities: OlxLocation[] = [];
  protected advertForm!: FormGroup<AdvertForm>;
  protected actionType!: AdvertActionType;
  protected channelId!: string;
  protected advert?: OlxAdvertItem;
  protected draft?: OlxAdvertActionDraft;
  protected selectedLocation?: OlxLocation;
  protected selectedCategory?: OlxCategory;
  protected selectedCategoryPathText?: string;
  protected selectedPriceAttribute?: OlxCategoryAttribute;
  protected selectedSalaryAttribute?: OlxCategoryAttribute;
  protected selectedCategoryAttributes?: OlxCategoryAttribute[];
  protected isSaving: boolean = false;
  protected plannerId?: string;
  protected loaders = {
    categories: true,
    attributes: false,
    cities: false,
    action: false,
    description: false,
  };

  constructor(
    private readonly modalService: ModalService,
    private readonly powerOlxV2Service: PowerOlxV2Service,
    private readonly plannerService: PlannerService,
    private readonly translate: TranslateService,
    private readonly requestHelper: RequestHelper,
    private readonly notificationService: NotificationService,
    private readonly aiService: AiService,
    protected readonly baseService: BaseService,
  ) {}

  ngOnInit(): void {
    this.initPayload();
    this.initForm();
    this.initCategories();

    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  ngOnDestroy(): void {
    this.saveFormToLocalStorage();

    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleBeforeUnload = () => {
    this.saveFormToLocalStorage();
  };

  private saveFormToLocalStorage(): void {
    if (this.isSaving || this.actionType !== AdvertActionType.Create) return;

    const data: OlxAdvertActionDraft = {
      advert: this.advertForm.getRawValue(),
      ...(this.selectedLocation && { selectedLocation: this.selectedLocation }),
    };

    localStorage.setItem(DRAFT_ADVERT_LOCAL_STORAGE_KEY, JSON.stringify(data));
  }

  private initPayload(): void {
    this.actionType = this.payload.action_type;
    this.channelId = this.payload.channel_id;
    this.advert = this.payload.advert;
    this.plannerId = this.payload.planner_id;

    this.getAdvert();
  }

  private initForm(): void {
    if (this.actionType === AdvertActionType.Create) {
      const stored = localStorage.getItem(DRAFT_ADVERT_LOCAL_STORAGE_KEY);
      this.draft = stored && JSON.parse(stored);
    }

    const advert = this.draft?.advert || this.advert;
    this.selectedLocation = this.draft?.selectedLocation || this.advert?.location;

    this.advertForm = new FormGroup<AdvertForm>({
      title: new FormControl(advert?.title ?? null, [
        Validators.minLength(16),
        Validators.maxLength(150),
        Validators.required,
      ]),
      description: new FormControl(advert?.description ?? null, [
        Validators.minLength(40),
        Validators.maxLength(9000),
        Validators.required,
      ]),
      category_id: new FormControl(advert?.category_id ?? null, Validators.required),
      images: new FormControl(advert?.images ?? []),
      contact: new FormGroup({
        name: new FormControl(advert?.contact?.name ?? null, Validators.required),
        phone: new FormControl(advert?.contact?.phone ?? null, phoneValidator),
      }),
      location: new FormControl(
        {
          city_id: advert?.location?.city_id ?? null,
          district_id: advert?.location?.district_id ?? null,
        },
        [locationRequiredValidator, Validators.required],
      ),
      advertiser_type: new FormControl(advert?.advertiser_type ?? OlxAdvertiserType.PRIVATE, Validators.required),
      auto_extend_enabled: new FormControl(advert?.auto_extend_enabled ?? null),
    });

    if (this.actionType === AdvertActionType.Update && this.advert?.advertiser_type === OlxAdvertiserType.BUSINESS) {
      this.advertiserTypeValues = ADVERTISER_TYPE_VALUES.filter((v) => v.value === OlxAdvertiserType.BUSINESS);

      this.advertForm.controls.advertiser_type.disable();
    }
  }

  protected initCategories(): void {
    this.requestHelper.handle<OlxCategory[]>(
      this.powerOlxV2Service.getCategories(),
      (res) => {
        this.categories = res;

        const advert = this.draft?.advert || this.advert;
        if (advert?.category_id) {
          this.advertForm.controls.category_id.setValue(advert.category_id);
        }
      },
      {
        key: 'power_olx_advert_action_categories',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'categories',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_categories',
      },
    );
  }

  protected onPriceFormReady(form: FormGroup<PriceForm>): void {
    if (this.advertForm.controls.price) {
      this.advertForm.setControl('price', form);
    } else {
      this.advertForm.addControl('price', form);
    }
  }

  protected onLocationChanged(location: OlxLocation): void {
    this.selectedLocation = location;
  }

  private enforcePhotoLimit(): void {
    const photosLimit = this.selectedCategory?.photos_limit || 0;
    const images = this.advertForm.controls.images.getRawValue() || [];
    this.advertForm.controls.images.setValue(images.slice(0, photosLimit));
  }

  private loadCategoryAttributes(categoryId: number): void {
    this.selectedPriceAttribute = this.selectedSalaryAttribute = undefined;
    this.selectedCategoryAttributes = [];

    this.requestHelper.handle<OlxCategoryAttribute[]>(
      this.powerOlxV2Service.getCategoryAttributes(categoryId),
      (attrs = []) => {
        this.selectedCategoryAttributes = attrs;

        for (const attr of attrs) {
          if (attr.type === OlxCategoryAttributeType.PRICE) {
            this.selectedPriceAttribute = attr;
          } else if (attr.type === OlxCategoryAttributeType.SALARY) {
            this.selectedSalaryAttribute = attr;
          }
        }
      },
      {
        key: 'power_olx_category_attributes',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'attributes',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_category_attributes',
      },
    );
  }

  protected onCategoryChanged(event: SelectedCategoryEvent): void {
    this.selectedCategory = event.selectedCategory;
    this.selectedCategoryPathText = event.selectedCategoryPathText;

    this.enforcePhotoLimit();
    this.loadCategoryAttributes(event.selectedCategory.category_id);
  }

  protected searchCity(term: string): void {
    if (!term) {
      this.cities = [];
      return;
    }

    this.requestHelper.handle<OlxLocation[]>(
      this.powerOlxV2Service.getCities({
        search: term,
        page: 1,
        take: 10,
      }),
      (cities = []) => {
        this.cities = cities;
      },
      {
        key: 'power_olx_category_advert_action_cities',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'cities',
        defaultErrorMessage: 'notifications_texts.error.failed_to_retrieve_cities',
      },
    );
  }

  protected onGenerateDescription(): void {
    if (this.loaders.description) return;

    const payload: GenerateAdvertDescriptionPayload = {
      channel_id: this.channelId,
      advert: this.advertForm.getRawValue() as OlxAdvertActionPayload,
      ...(this.selectedLocation && { location: this.selectedLocation }),
      ...(this.selectedCategoryPathText && { category_path_text: this.selectedCategoryPathText }),
    };

    this.requestHelper.handle<string>(
      this.aiService.generateAIResponse({
        task: AI_TASK_TYPE.ADVERT_DESCRIPTION,
        x_data: {
          text: payload.advert.title || '',
        },
      }),
      (text) => {
        const generated = text?.trim();
        if (!generated) return;

        this.advertForm.controls.description.setValue(generated);
      },
      {
        key: 'advert_generate_description',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'description',
        defaultErrorMessage: 'notifications_texts.error.failed_to_generate_ai_answer',
      },
    );
  }

  protected createAdvert() {
    this.requestHelper.handle<void>(
      this.powerOlxV2Service.createAdvert(this.channelId, this.advertForm.getRawValue()),
      () => {
        this.isSaving = true;
        localStorage.removeItem(DRAFT_ADVERT_LOCAL_STORAGE_KEY);
        this.notificationService.showSuccess(
          this.translate.instant('notifications_texts.success.success'),
          this.translate.instant('notifications_texts.success.successfully_created'),
        );
        this.onCloseSidebar('success');
      },
      {
        key: 'power_olx_create_advert',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'action',
        defaultErrorMessage: 'notifications_texts.error.failed_to_create_advert',
      },
    );
  }

  protected updateAdvert() {
    let fetch$ = this.powerOlxV2Service.updateAdvert(this.channelId, this.advert!.id, this.advertForm.getRawValue());

    if (this.plannerId) {
      fetch$ = this.plannerService.updatePlannerPayload(this.plannerId, {
        advert: this.advertForm.getRawValue(),
        location: this.selectedLocation,
      });
    }

    this.requestHelper.handle<void>(
      fetch$,
      () => {
        this.notificationService.showSuccess(
          this.translate.instant('notifications_texts.success.success'),
          this.translate.instant('notifications_texts.success.successfully_updated'),
        );
        this.onCloseSidebar('success');
      },
      {
        key: 'power_olx_update_advert',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'action',
        defaultErrorMessage: 'notifications_texts.error.failed_to_update',
      },
    );
  }

  private getAdvert(): void {
    const advertId = this.advert?.id;
    if (!advertId) return;

    this.requestHelper.handle<OlxAdvertItem>(
      this.powerOlxV2Service.getAdvert(this.channelId, advertId),
      (advert) => {
        this.advert = advert;
        this.initForm();
      },
      {
        key: `power_olx_get_advert_${advertId}`,
        destroy$: this.destroy$,
        defaultErrorMessage: 'notifications_texts.error.failed_to_get_advert',
      },
    );
  }

  protected onSave(): void {
    if (this.advertForm.invalid) {
      this.advertForm.markAllAsTouched();
      return;
    }

    switch (this.actionType) {
      case AdvertActionType.Create: {
        this.createAdvert();
        break;
      }
      case AdvertActionType.Update: {
        this.updateAdvert();
        break;
      }
    }
  }

  protected onOpenPlanner(): void {
    if (this.advertForm.invalid) {
      this.advertForm.markAllAsTouched();
      return;
    }

    const modalData: ModalData<SidebarData<PlannerSidebarData>> = {
      component: StackedSidebarComponent,
      width: '50vw',
      hideHeader: true,
      data: {
        type: SidebarType.Planner,
        payload: {
          mode: PlannerSidebarMode.Create,
          scope: PLANNER_SCOPE.OLX,
          actions: [PLANNER_OLX_ACTIONS_REGISTRY[PLANNER_OLX_ACTION.CREATE_ADVERT]],
          payloads: [
            {
              channel_id: this.channelId,
              advert: this.advertForm.getRawValue(),
              location: this.selectedLocation,
            },
          ],
        },
      },
    };

    this.loaders.action = true;
    this.modalService
      .openModal(modalData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.loaders.action = false;

        if (res !== 'success') return;

        this.isSaving = true;
        localStorage.removeItem(DRAFT_ADVERT_LOCAL_STORAGE_KEY);
        this.onCloseSidebar(res);
      });
  }

  protected onCancel(): void {
    this.onCloseSidebar();
  }

  protected onCloseSidebar(status?: string): void {
    this.close.emit(status);
  }

  protected readonly AdvertActionType = AdvertActionType;
  protected readonly Boolean = Boolean;
  protected advertiserTypeValues = ADVERTISER_TYPE_VALUES;
}
